import type { FormBlockContent, FormField } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Card, CardContent, CardHeader } from "@/components/ui/card"

interface FormBlockEditorProps {
  content: FormBlockContent | null
  onChange: (content: FormBlockContent) => void
}

const FIELD_TYPES: FormField["type"][] = ["text", "email", "textarea", "select"]

export function FormBlockEditor({ content, onChange }: FormBlockEditorProps) {
  const c = content ?? {}
  const fields = (c.fields ?? []) as FormField[]
  const update = (partial: Partial<FormBlockContent>) =>
    onChange({ ...c, ...partial })

  const setField = (i: number, field: keyof FormField, val: unknown) => {
    const next = [...fields]
    if (!next[i]) next[i] = { name: "", type: "text" }
    next[i] = { ...next[i], [field]: val }
    update({ fields: next })
  }
  const addField = () =>
    update({ fields: [...fields, { name: `field_${fields.length + 1}`, type: "text" }] })
  const removeField = (i: number) =>
    update({ fields: fields.filter((_, j) => j !== i) })

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Contact Form</h3>
        <p className="text-sm text-muted-foreground">
          Form fields for the contact page.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? "Get in touch"}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Get in touch"
          />
        </div>
        <div>
          <Label>Submit button text</Label>
          <Input
            value={c.submit_label ?? "Send message"}
            onChange={(e) => update({ submit_label: e.target.value })}
            placeholder="Send message"
          />
        </div>
        <div className="space-y-3">
          <Label>Fields</Label>
          {fields.map((f, i) => (
            <div key={i} className="rounded-md border p-3 space-y-2">
              <Input
                placeholder="Field name"
                value={f.name}
                onChange={(e) => setField(i, "name", e.target.value)}
              />
              <Input
                placeholder="Label"
                value={f.label ?? ""}
                onChange={(e) => setField(i, "label", e.target.value)}
              />
              <Select
                value={f.type}
                onValueChange={(v) => setField(i, "type", v as FormField["type"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={f.required ?? false}
                  onChange={(e) => setField(i, "required", e.target.checked)}
                />
                <Label>Required</Label>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeField(i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addField}>
            Add field
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
